import {orderTypes} from 'utils/proptypes';
import PaginateTable from 'components/table/paginate';
import {HiOutlineDuplicate, HiOutlinePencil, HiOutlineTrash} from 'react-icons/hi';
import {useEffect, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {initOrder} from 'utils/initData';
import SpinnerComponent from 'components/spinner';
import {apiStatus, defaultpageCount, permissionKeys, routePaths, sortByTypeType} from 'utils/constants';
import {toast} from 'react-toastify';
import {checkOrgAdmin, checkPermission, checkUseRegionAndLocation, copyToClipboard, formatDateNotTime, formatMoney, getUserOrg, messageErrors} from '../../utils/utils';
import {useSelector} from 'react-redux';
import {locationSelector, orgSelector, userSelector} from 'redux/selectors';
import useUser from 'hooks/useUser';
import '../../styles/styles.scss';
import {Table} from 'flowbite-react';
import Sort from 'components/table/sort';
import EmptyState from 'components/commonComponent/emptyState';
import NoResult from 'components/commonComponent/noResult';
import {createPurchaseOrder, deletePurchaseOrderById, getPurchaseOrdersByFilter} from 'api/purchaseOrderApi';
import GroupButtonWithIcons from 'components/button/buttonGroupWithIcons';
import Status from "../../components/commonComponent/Status";
import {Button} from "primereact/button";
import CreatePurchaseOrderModal from 'components/modal/purchaseOrderModal/createPurchaseOrderModal';
import ActionTableWithFilter from 'components/table/actionTableWithFilter';
import ConfirmModal from 'components/modal/confirmModal';
import { getOrgVendorsByFilter } from 'api/vendorApi';
import MultiSelect from 'components/select/index';

const OrderEmpty = require('../../assets/image/svg/shopping-cart.svg').default || '';
const locationEmty = require('../../assets/image/svg/location-marker.svg').default || '';

const PurchaseOrderRequestedList = (props: any) => {
    const queryClient = useQueryClient();
    const {WRITE_PURCHASE_ORDER, REVIEW_PURCHASE_ORDER, PLATFORM_ADMIN} = permissionKeys;
    const [t] = useTranslation();
    const {handleSetTookie} = useUser();
    const {userInfo} = useSelector(userSelector);
    const [searchValue, setSearchValue] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalEntities, setTotalEntities] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [openModalConfirm, setOpenModalConfirm] = useState(false);
    const [idDelete, setIdDelete] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isBlank, setIsBlank] = useState(false);
    const [isLastOrder, setIsLastOrder] = useState(false);
    const [purchaseOrders, setPurchaseOrders] = useState([initOrder]);
    const [data, setData] = useState<any>();
    const [sortBy, setSortBy] = useState('');
    const [sortByType, setSortByType] = useState('');
    const navigate = useNavigate();
    const [pageCount, setPageCount] = useState(defaultpageCount);
    const {organizationId} = useSelector(orgSelector);
    const isWriteOrg = checkPermission(userInfo, props.type, [WRITE_PURCHASE_ORDER], organizationId);
    const isReviewPurchaseOrder = checkPermission(userInfo, props.type, [REVIEW_PURCHASE_ORDER], organizationId);
    const {locationId} = useSelector(locationSelector);
    const [location, setLocation] = useState('');
    const [region, setRegion] = useState('');
    const [fDate, setFDate] = useState('');
    const [tDate, setTDate] = useState('');
    const [statuss, setStatuss] = useState([{name: 'Requested', code: 'Requested'}]);
    const [poStatus, setPoStatus] = useState('Requested');
    const isAdmin = checkPermission(userInfo, props.type, [PLATFORM_ADMIN], organizationId);
    const isOrgAdmin = checkOrgAdmin(userInfo, organizationId, isAdmin);
    const userAccessLocationOrRegion = checkUseRegionAndLocation(userInfo, organizationId, isOrgAdmin);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [vendorTypeOptions, setVendorTypeOptions] = useState<any[]>([]);
    const [selectedVendorForCreate, setSelectedVendorForCreate] = useState<any>(null);
    const [isVendorOptionsLoaded, setIsVendorOptionsLoaded] = useState(false);
    const selectedOrganizationVendorId = selectedVendorForCreate?.value || '';

    const filterPurchaseOrdersBySelectedVendor = (orders: any[] = []) => {
        if (!selectedOrganizationVendorId) return orders;
        return orders.filter((po: any) => po?.organizationVendorId === selectedOrganizationVendorId);
    };

    useEffect(() => {
        setIsLoading(true);
        getPurchaseOrdersByFilter({
            page: currentPage,
            limit: pageCount,
            searchQuery: searchValue,
            sortBy,
            sortByType,
            organizationId,
            locationId: location,
            regionId: region,
            fDate,
            tDate,
            statuss: poStatus,
            organizationVendorId: selectedOrganizationVendorId,
        }).then(res => {
            setData(res.data);
            setError(false);
            setIsLoading(false);
        }).catch(err => {
            setError(err);
            setIsLoading(false);
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, region, location, fDate, tDate, poStatus, currentPage, searchValue, sortBy, sortByType, selectedOrganizationVendorId])

    const checkIsEmployee = (user: any) => {
        const userOrg = user.organizations?.find((x: any) => x.id === organizationId);
        let isCreateOrder: boolean = true;
        if (userOrg) {
            if (userOrg.role?.displayName === 'Employee') {
                isCreateOrder = false;
            }
        }

        return isCreateOrder;
    }

    const isEmployee = checkIsEmployee(userInfo);

    const handleGoToCreatePurchaseOrderPage = () => {
        if (!isVendorOptionsLoaded) return;
        if (vendorTypeOptions?.length === 0) {
            toast.error('This organization has no vendor. Contact site admin.');
            return;
        }
        navigate(routePaths.PURCHASE_ORDER_CREATE_PAGE);
    };

    const handleSelectVendorInline = (option: any) => {
        setSelectedVendorForCreate(option || null);
        setCurrentPage(0);
    };

    const handleEdit = (id: string) => {
        navigate(`${routePaths.PURCHASE_ORDER_DETAIL}/${id}`)
    };

    const handleReview = (id: string) => {
        navigate(`${routePaths.PURCHASE_ORDER_REVIEW}/${id}`)
    };

    const handleDuplicatePO = async (payload: any) => {
        const request = {
            name: `Order By ${userInfo.firstName} ${userInfo.lastName} - Duplicate`,
            locationId: payload.locationId,
            location: {id: payload.locationId},
            organizationVendorId: payload.organizationVendorId || selectedOrganizationVendorId || '',
            status: 'Draft',
            purchaseOrderItems: payload?.purchaseOrderItems?.map((i: any) => {
                return {itemId: i.itemId, quantityOrdered: i.quantityOrdered, quantityReceived: 0};
            }),
        };

        const res = await createPurchaseOrder(request);
        if (res.status >= apiStatus.SUCCESS) {
            await queryClient.invalidateQueries(['getPurchaseOrders'], {refetchType: 'active'}, {cancelRefetch: true});
            const message: string = t('orderPage.duplicateSuccess');
            toast.success(message);
            handleEdit(res.data?.id);
        } else {
            toast.error(`${t('orderPage.duplicateFail')}`);
        }
    }

    const handleDelete = async (id: string) => {
        setIdDelete(id);
        setOpenModalConfirm(!openModalConfirm);
    };

    const onDelete = async (id: string) => {
        setIsDeleting(true);
        try {
            const response = await deletePurchaseOrderById(id);
            if (response.status === 200) {
                // Manually refetch the data since this page doesn't use React Query
                setIsLoading(true);
                getPurchaseOrdersByFilter({
                    page: currentPage,
                    limit: pageCount,
                    searchQuery: searchValue,
                    sortBy,
                    sortByType,
                    organizationId,
                    locationId: location,
                    regionId: region,
                    fDate,
                    tDate,
                    statuss: poStatus,
                    organizationVendorId: selectedOrganizationVendorId,
                }).then(res => {
                    setData(res.data);
                    setIsLoading(false);
                }).catch(err => {
                    setError(err);
                    setIsLoading(false);
                });
                
                const message: string = t('orderPage.deleteSuccessfully')
                toast.success(message);
                setOpenModalConfirm(!openModalConfirm);
            }
        } catch (error: any) {
            const message: string = `${error.response.data.errors?.[0].detail}`;
            toast.error(message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSearch = (value: any) => {
        setSearchValue(value);
        setCurrentPage(0);
    };

    const handleCopyToClipboard = (e: any, item: any, type: string) => {
        switch (type) {
            case 'id':
                copyToClipboard(item.id, t('orderPage.copiedId'));
                break;
        }
        e.stopPropagation();
    };

    const handleHeaderSort = (field: string) => {
        setSortBy(field);
        let type = '';
        if (field !== sortBy) {
            type = sortByTypeType.ASC;
        } else if (sortByType === sortByTypeType.ASC) {
            type = sortByTypeType.DESC;
        } else {
            setSortBy(type);
        }
        setSortByType(type);
    };

    useEffect(() => {
        getOrgVendorsByFilter({page: 0, limit: 1000, sortByType: sortByTypeType.DESC, organizationId})?.then(res => {
            const newOption = res?.data?.entities?.map((i: any) => ({
                label: i.name,
                value: i.id,
            }));
            setVendorTypeOptions(newOption || []);
            setIsVendorOptionsLoaded(true);
        }).catch(() => {
            setVendorTypeOptions([]);
            setIsVendorOptionsLoaded(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    // Keep vendor filter opt-in; auto-select can unintentionally hide orders.
    useEffect(() => {
        setSelectedVendorForCreate(null);
    }, [organizationId]);

    const renderVendorInlineFilter = () => {
        if (!(isWriteOrg && isEmployee)) return null;
        if (!isVendorOptionsLoaded) return null;
        if (vendorTypeOptions?.length === 0) return null;

        return (
            <MultiSelect
                className="!flex-none !w-[220px]"
                onChange={handleSelectVendorInline}
                options={vendorTypeOptions}
                value={selectedVendorForCreate}
                isHolderLabel={false}
                placeholder={t('vendorAssignmentPage.selectVendor')}
                noOptionsMessage={() => t('No options')}
            />
        );
    };

    const hasNoVendors = isVendorOptionsLoaded && vendorTypeOptions?.length === 0;
    const emptyStateTitle = hasNoVendors
        ? 'This organization has no vendor. Contact site admin.'
        : t('orderPage.titleNoResult');
    const emptyStateSubtitle = hasNoVendors ? '' : t('orderPage.subTitleNoResult');
    const isDefaultRequestedView =
        searchValue === '' &&
        location === '' &&
        region === '' &&
        fDate === '' &&
        tDate === '' &&
        poStatus === 'Requested';

    useEffect(() => {
        let poStas = '';
        for (let index = 0; index < statuss?.length; index++) {
            const status: any = statuss[index];
            if (poStas === '') {
                poStas = status.code;
            } else {
                poStas = `${poStas},${status.code}`;
            }
        }
        setPoStatus(poStas);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statuss]);

    useEffect(() => {
        if (data) {
            const entities = data?.entities || [];
            const filteredEntities = filterPurchaseOrdersBySelectedVendor(entities);
            setPurchaseOrders(filteredEntities);
            setTotalEntities(selectedOrganizationVendorId ? filteredEntities.length : (data?.totalEntities || 0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, selectedOrganizationVendorId]);

    useEffect(() => {
        setTotalEntities(data?.totalEntities);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (isBlank === true) {
            navigate(`${routePaths.PURCHASE_ORDER_DETAIL}/blank-new`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBlank]);

    useEffect(() => {
        if (isLastOrder === true) {
            getPurchaseOrdersByFilter({
                page: currentPage,
                limit: 10,
                searchQuery: searchValue,
                sortBy,
                sortByType,
                locationId,
                organizationVendorId: selectedOrganizationVendorId,
            }).then((res: any) => {
                if (res?.data?.totalEntities > 0) {
                    navigate(`${routePaths.PURCHASE_ORDER_DETAIL}/${res.data?.entities[0].id}`);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLastOrder]);

    return (
        <>
            {userAccessLocationOrRegion === true
            ?
            (
                <>
                    {error ? <EmptyState buttonName={t('stockPage.createOrder')}
                                 handleAddClick={hasNoVendors ? null : (isWriteOrg && isEmployee ? handleGoToCreatePurchaseOrderPage : null)}
                                 title={emptyStateTitle}
                                 subtitle={emptyStateSubtitle}
                                 icon={OrderEmpty}
                    /> :
                    totalEntities === 0 ? (
                        hasNoVendors || isDefaultRequestedView ? (
                            <EmptyState buttonName={t('stockPage.createOrder')}
                                        handleAddClick={hasNoVendors ? null : (isWriteOrg && isEmployee ? handleGoToCreatePurchaseOrderPage : null)}
                                        title={emptyStateTitle}
                                        subtitle={emptyStateSubtitle}
                                        icon={OrderEmpty}
                            />
                        ) : (
                            <div className="main-sub">
                                <ActionTableWithFilter
                                    placeholderSearch={t('orderPage.placeholderSearch')}
                                    buttonName={t('stockPage.createOrder')}
                                    renderExtraFilter={renderVendorInlineFilter()}
                                    buttons={[
                                        {
                                            buttonName: t('stockPage.createOrder'),
                                            buttonAction: handleGoToCreatePurchaseOrderPage,
                                            buttonHidden: isWriteOrg && isEmployee && isVendorOptionsLoaded && vendorTypeOptions?.length > 0,
                                            className: 'text-white bg-secondary w-[220px]',
                                        }
                                    ]}
                                    handleSearch={handleSearch}
                                    location={location}
                                    setLocation={setLocation}
                                    region={region}
                                    setRegion={setRegion}
                                    fdate={fDate}
                                    setFDate={setFDate}
                                    tDate={tDate}
                                    setTDate={setTDate}
                                    statuss={statuss}
                                    setStatuss={setStatuss}
                                />
                                <NoResult />
                            </div>
                        )
                    ) : <div className="main-sub">
                            <ActionTableWithFilter
                                placeholderSearch={t('orderPage.placeholderSearch')}
                                buttonName={t('stockPage.createOrder')}
                                renderExtraFilter={renderVendorInlineFilter()}
                                buttons={[
                                    {
                                        buttonName: t('stockPage.createOrder'),
                                        buttonAction: handleGoToCreatePurchaseOrderPage,
                                        buttonHidden: isWriteOrg && isEmployee && isVendorOptionsLoaded && vendorTypeOptions?.length > 0,
                                        className: 'text-white bg-secondary w-[220px]',
                                    }
                                ]}
                                handleSearch={handleSearch}
                                location={location}
                                setLocation={setLocation}
                                region={region}
                                setRegion={setRegion}
                                fdate={fDate}
                                setFDate={setFDate}
                                tDate={tDate}
                                setTDate={setTDate}
                                statuss={statuss}
                                setStatuss={setStatuss}
                            />
                            <Table className="border-b">
                                <Table.Head className="text-xs text-gray-900 border-b-2 border-gray-100">
                                    <Table.HeadCell className="cursor-pointer w-80 bg-white font-medium text-xs px-3"
                                                    onClick={() => handleHeaderSort('vendorOrderId')}>
                                        <div className="flex items-center ">
                                            {t('orderPage.orderId').toUpperCase()}
                                            <Sort check={sortBy === 'vendorOrderId'} sortByType={sortByType}/>
                                        </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell className="cursor-pointer w-60 bg-white font-medium text-xs"
                                                    onClick={() => handleHeaderSort('id')}>
                                        <div className="flex items-center ">
                                            {t('orderPage.region').toUpperCase()}
                                        </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell className="cursor-pointer w-60 bg-white font-medium text-xs"
                                                    onClick={() => handleHeaderSort('id')}>
                                        <div className="flex items-center ">
                                            {t('orderPage.location').toUpperCase()}
                                        </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="hidden-mobile w-60 bg-white font-medium text-xs"
                                        onClick={() => {
                                            handleHeaderSort('orderedOn');
                                        }}
                                    >
                                        <div className="flex items-center">
                                            {t('orderPage.orderDate').toUpperCase()}
                                            <Sort check={sortBy === 'OrderedOn'} sortByType={sortByType}/>
                                        </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="hidden-mobile w-60 bg-white font-medium text-xs"
                                        onClick={() => {
                                            handleHeaderSort('status');
                                        }}
                                    >
                                        <div className="flex items-center">
                                            {t('orderPage.status').toUpperCase()}
                                            <Sort check={sortBy === 'Status'} sortByType={sortByType}/>
                                        </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="hidden-mobile w-60 bg-white font-medium text-xs"
                                        >
                                            <div className="flex items-center">
                                                {t('orderPage.total').toUpperCase()}
                                            </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell className="w-8 bg-white">{''}</Table.HeadCell>
                                </Table.Head>
                                <Table.Body>
                                    {purchaseOrders.map((item: orderTypes, index) => (
                                        <Table.Row key={`row-${index + 1}`}
                                                className="bg-white text-sm hover:bg-gray-50 border-b border-gray-100">
                                            <Table.Cell className="font-semibold py-[10px] px-3">
                                                <div className="flex flex-row items-center"
                                                    onClick={() => isReviewPurchaseOrder ? handleReview(item.id) : handleEdit(item.id)}>
                                                    <div className="text-secondary">{item?.vendorOrderId ? `Order #${item?.vendorOrderId}` : item?.id}</div>
                                                    <div
                                                        className="copy-icon pl-3 cursor-pointer text-gray-800">
                                                        <HiOutlineDuplicate size={25}
                                                                            onClick={(e: any) => handleCopyToClipboard(e, item, 'id')}/>
                                                    </div>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell className="py-[10px]">
                                                <div className="flex flex-row items-center"
                                                    onClick={() => isReviewPurchaseOrder ? handleReview(item.id) : handleEdit(item.id)}>
                                                    <div className="text-black">{item.location?.region?.name}</div>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell className="py-[10px]">
                                                <div className="flex flex-row items-center"
                                                    onClick={() => isReviewPurchaseOrder ? handleReview(item.id) : handleEdit(item.id)}>
                                                    <div className="text-black">{item.location?.name}</div>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell className="py-[10px]">
                                                <div className="flex flex-row items-center"
                                                    onClick={() => isReviewPurchaseOrder ? handleReview(item.id) : handleEdit(item.id)}>
                                                    <div
                                                        className="text-black">{formatDateNotTime(item?.created?.toString() || '')}</div>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell className="py-[10px]">
                                                <Status type={item?.status}/>

                                            </Table.Cell>
                                            <Table.Cell className="py-[10px]">
                                                <div className="flex flex-row items-center"
                                                    onClick={() => isReviewPurchaseOrder ? handleReview(item.id) : handleEdit(item.id)}>
                                                    <div
                                                        className="text-black">{formatMoney(item.orderTotal || 0)}</div>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell className="flex items-center justify-end">
                                                {item?.status === 'Requested' && isReviewPurchaseOrder && (
                                                    <Button
                                                        className="bg-primary px-[15px] h-[34px] text-white text-sm mr-4"
                                                        type="button" onClick={() => handleReview(item.id)}>
                                                        {t('Review')}
                                                    </Button>
                                                )}
                                                {isWriteOrg && (
                                                    <GroupButtonWithIcons
                                                        className="items-start justify-self-start w-[132px]"
                                                        buttons={[
                                                            {
                                                                type: 'button',
                                                                text: '',
                                                                classType: 'white',
                                                                icon: <HiOutlineDuplicate size={"20px"}/>,
                                                                action: () => item.status === 'Draft' ? undefined : handleDuplicatePO(item),
                                                                visible: item.status === 'Draft' ? true : false
                                                            },
                                                            {
                                                                type: 'button',
                                                                text: '',
                                                                classType: 'white',
                                                                icon: <HiOutlinePencil size={"20px"}/>,
                                                                action: () => isReviewPurchaseOrder ? handleReview(item.id) : handleEdit(item.id),
                                                            },
                                                            {
                                                                type: 'submit',
                                                                text: '',
                                                                classType: 'white',
                                                                icon: <HiOutlineTrash size={"20px"}/>,
                                                                action: () => item.status === 'Ordered' || item.status === 'Received' || item.status === 'Rejected' || item.status === 'Partially Received' || item.status === 'Requested' ? undefined : handleDelete(item?.id),
                                                                visible: item.status === 'Ordered' || item.status === 'Received' || item.status === 'Rejected' || item.status === 'Partially Received' || item.status === 'Requested' ? true : false
                                                            },
                                                        ]}
                                                    />
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                            {isLoading && <SpinnerComponent/>}
                            <PaginateTable
                                setCurrentPage={setCurrentPage}
                                currentPage={currentPage}
                                totalEntities={totalEntities}
                                isLoadingTable={isLoading}
                            />
                    </div>}
                {openModal && (
                    <CreatePurchaseOrderModal
                        headerTitle={t('orderPage.createNewOrder')}
                        openModal={openModal}
                        isEdit={isEdit}
                        isBlank={isBlank}
                        setIsBlank={setIsBlank}
                        isLastOrder={isLastOrder}
                        setIsLastOrder={setIsLastOrder}
                        setOpenModal={setOpenModal}
                        queryClient={queryClient}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                    />
                )}
                {openModalConfirm && (
                    <ConfirmModal
                        openModal={openModalConfirm}
                        setOpenModal={setOpenModalConfirm}
                        onDelete={() => onDelete(idDelete)}
                        isLoading={isDeleting}
                    />
                )}
                </>
            )
            :
            (
                <div className="h-full pt-16">
                    <div className="mb-4 flex justify-center">
                        <img alt="MileMarker logo" src={locationEmty}/>
                    </div>
                    <p className="text-2xl font-semibold text-center mb-4">{t('dashboard.notificationNotLocation')}</p>
                    <p className="text-sm font-normal text-center	">{t('dashboard.noteNotLocation')}</p>
                </div>
            )
            }
        </>
    );
};

export default PurchaseOrderRequestedList;
